import {
  Color,
  type Material,
  type Mesh,
  type Object3D,
  type Camera,
  type Intersection,
  Raycaster,
  Vector2,
  type Vector3,
} from 'three';
import { SELECTION_CONFIG } from '../config';

export interface SelectionInfo {
  expressID: number | null;
  meshName: string;
}

export type SelectionListener = (info: SelectionInfo | null) => void;

interface MaterialWithEmissive {
  emissive?: Color;
  emissiveIntensity?: number;
}

export class Selection {
  private readonly raycaster = new Raycaster();
  private readonly ndc = new Vector2();
  private readonly downPos = new Vector2();
  private listener: SelectionListener | null = null;

  private current: Mesh | null = null;
  private originalMaterial: Material | Material[] | null = null;
  private hovered: Mesh | null = null;
  private hoveredOriginalMaterial: Material | Material[] | null = null;
  private hoverRafId: number | null = null;
  private lastMove: { x: number; y: number } | null = null;
  private enabled = true;

  get currentMesh(): Mesh | null {
    return this.current;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** Disabled while another tool (e.g. measuring) owns pointer input. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearHover();
      this.canvas.style.cursor = '';
    }
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.downPos.set(e.clientX, e.clientY);
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const dx = e.clientX - this.downPos.x;
    const dy = e.clientY - this.downPos.y;
    if (Math.hypot(dx, dy) > SELECTION_CONFIG.clickPixelThreshold) return;
    if (e.button !== 0) return;
    this.pickAt(e.clientX, e.clientY);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    this.lastMove = { x: e.clientX, y: e.clientY };
    if (this.hoverRafId !== null) return;
    this.hoverRafId = requestAnimationFrame(() => {
      this.hoverRafId = null;
      if (this.lastMove) this.updateHover(this.lastMove.x, this.lastMove.y);
    });
  };

  private readonly onPointerLeave = (): void => {
    this.lastMove = null;
    this.clearHover();
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly getCamera: () => Camera,
    private readonly root: Object3D,
    private readonly invalidate: () => void = () => {},
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerleave', this.onPointerLeave);
  }

  onChange(listener: SelectionListener | null): void {
    this.listener = listener;
  }

  pickAt(clientX: number, clientY: number): void {
    const mesh = this.raycastMesh(clientX, clientY);
    if (!mesh) {
      this.deselect();
      return;
    }
    this.select(mesh);
  }

  private raycastMesh(clientX: number, clientY: number): Mesh | null {
    const hit = this.raycastHit(clientX, clientY);
    return hit ? (hit.object as Mesh) : null;
  }

  /** World-space point under the pointer, for callers that steer the camera. */
  raycastPoint(clientX: number, clientY: number): Vector3 | null {
    const hit = this.raycastHit(clientX, clientY);
    return hit ? hit.point.clone() : null;
  }

  private raycastHit(clientX: number, clientY: number): Intersection | null {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.getCamera());
    const hits = this.raycaster.intersectObject(this.root, true);
    return hits.find((h) => (h.object as Mesh).isMesh && h.object.visible) ?? null;
  }

  private updateHover(clientX: number, clientY: number): void {
    const mesh = this.raycastMesh(clientX, clientY);
    if (mesh === this.hovered || mesh === this.current) {
      if (mesh === this.current) this.clearHover();
      this.canvas.style.cursor = mesh ? 'pointer' : '';
      return;
    }
    this.clearHover();
    if (!mesh) {
      this.canvas.style.cursor = '';
      return;
    }
    this.hovered = mesh;
    this.hoveredOriginalMaterial = mesh.material as Material | Material[];
    mesh.material = this.cloneAsHighlight(
      mesh.material as Material | Material[],
      SELECTION_CONFIG.hoverIntensity,
    );
    this.canvas.style.cursor = 'pointer';
    this.invalidate();
  }

  private clearHover(): void {
    if (this.hovered && this.hoveredOriginalMaterial) {
      this.disposeMaterial(this.hovered.material as Material | Material[]);
      this.hovered.material = this.hoveredOriginalMaterial;
      this.invalidate();
    }
    this.hovered = null;
    this.hoveredOriginalMaterial = null;
  }

  select(mesh: Mesh): void {
    if (this.current === mesh) return;
    if (mesh === this.hovered) this.clearHover();
    this.deselect();
    this.current = mesh;
    this.originalMaterial = mesh.material as Material | Material[];
    mesh.material = this.cloneAsHighlight(mesh.material as Material | Material[]);
    this.invalidate();
    this.notify();
  }

  deselect(): void {
    if (this.current && this.originalMaterial) {
      this.disposeMaterial(this.current.material as Material | Material[]);
      this.current.material = this.originalMaterial;
      this.invalidate();
    }
    if (this.current !== null) {
      this.current = null;
      this.originalMaterial = null;
      this.notify();
    }
  }

  private cloneAsHighlight(
    mat: Material | Material[],
    intensity: number = SELECTION_CONFIG.highlightIntensity,
  ): Material | Material[] {
    if (Array.isArray(mat)) return mat.map((m) => this.cloneOne(m, intensity));
    return this.cloneOne(mat, intensity);
  }

  private cloneOne(mat: Material, intensity: number): Material {
    const clone = mat.clone();
    const withEmissive = clone as Material & MaterialWithEmissive;
    if (withEmissive.emissive) {
      withEmissive.emissive = new Color(SELECTION_CONFIG.highlightColor);
      withEmissive.emissiveIntensity = intensity;
    }
    return clone;
  }

  private disposeMaterial(mat: Material | Material[]): void {
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat.dispose();
  }

  private notify(): void {
    if (!this.listener) return;
    if (!this.current) {
      this.listener(null);
      return;
    }
    const expressID =
      typeof this.current.userData['expressID'] === 'number'
        ? (this.current.userData['expressID'] as number)
        : null;
    this.listener({ expressID, meshName: this.current.name });
  }

  dispose(): void {
    this.deselect();
    this.clearHover();
    if (this.hoverRafId !== null) cancelAnimationFrame(this.hoverRafId);
    this.canvas.style.cursor = '';
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    this.listener = null;
  }
}
